import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Collapse } from '@mui/material';
import SectionAvatarSelector from './SectionAvatarSelector';
import SectionOrchestrator from './SectionOrchestrator';
import PRDGenerationButtons from '../PRDGenerationButtons';
import { api } from '../../../services/api';
import { normalizeSectionName } from '../../../utils/sectionColors';

/**
 * Convert string to sentence case, preserving special terms like UI/UX, API, CI/CD
 * @param {string} str - String to convert
 * @returns {string} String in sentence case with special terms preserved
 */
const toSentenceCase = (str) => {
  if (!str) return '';

  // First normalize special terms (UI/UX, API, CI/CD, etc.)
  const normalized = normalizeSectionName(str);

    // Check for UI/UX pattern directly (case-insensitive)
  const lowerStr = str.toLowerCase().trim();
  if (lowerStr === 'ui/ux' || lowerStr.includes('ui/ux')) {
    return 'UI/UX';
  }
  
  // If normalization changed the string, return the normalized version
  if (normalized !== str) {
    return normalized;
  }

  // Otherwise apply standard sentence case
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Component to display section header with title, description and avatar selection
 * @param {Object} props - Component props
 * @param {string} props.sectionName - Name of the section
 * @param {string} props.description - Description of the section
 * @param {string} props.sectionId - ID of the section
 * @param {string} props.projectId - ID of the project (for avatar uniqueness per project)
 * @param {Object} props.consolidatedAvatar - Pre-fetched avatar data from consolidated API
 * @param {Object} props.consolidatedOrchestrator - Pre-fetched orchestrator data from consolidated API
 * @param {boolean} props.useConsolidatedData - Whether to use consolidated data instead of individual API calls
 * @returns {React.ReactElement} Section header component
 */
const SectionHeader = ({ 
  sectionName, 
  description, 
  sectionId, 
  projectId, 
  consolidatedAvatar, 
  consolidatedOrchestrator, 
  useConsolidatedData 
}) => {
  // Initialize with null values - only use what comes from API
  const [sectionAvatar, setSectionAvatar] = useState({
    avatarUrl: null,
    avatarName: null
  });
  
  // Use consolidated data if available, otherwise fetch from API
  useEffect(() => {
    let cancelled = false;

    // If using consolidated data, use that instead of making API calls
    if (useConsolidatedData && consolidatedAvatar) {
      setSectionAvatar({
        avatarUrl: consolidatedAvatar.url,
        avatarName: consolidatedAvatar.name
      });
      return;
    }

    // Skip fetching if we don't have section ID or project ID
    if (!sectionId || !projectId) {
      return;
    }

    // Only fetch from API if not using consolidated data
    const fetchSectionAvatar = async () => {
      try {
        // Add timestamp to prevent browser caching
        const timestamp = Date.now();
        const response = await api.get(`/api/sections/${sectionId}/avatar?projectId=${projectId}&_t=${timestamp}`);

        if (cancelled) return;

        if (response.data && response.data.avatarUrl) {
          const newAvatar = {
            avatarUrl: response.data.avatarUrl,
            avatarName: response.data.avatarName
          };
          setSectionAvatar(newAvatar);
        } else {
          // If no avatar exists, set empty values
          setSectionAvatar({
            avatarUrl: null,
            avatarName: null
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching section avatar:', error);
        // Set empty values on error
        setSectionAvatar({
          avatarUrl: null,
          avatarName: null
        });
      }
    };

    fetchSectionAvatar();

    return () => { cancelled = true; };
  }, [sectionId, projectId, useConsolidatedData, consolidatedAvatar, sectionName]);
  
  // Handle avatar changes
  const handleAvatarChange = (newAvatarUrl, newAvatarName) => {
    setSectionAvatar({
      avatarUrl: newAvatarUrl,
      avatarName: newAvatarName
    });
  };
  
  return (
    <Box 
      sx={{ 
        width: { xs: '100%', md: '200px' },
        minWidth: { md: '200px' },
        position: 'relative',
        pt: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: { xs: 'center', md: 'flex-start' }
      }}
    >
      {/* Vertical divider line on right */}
      <Box 
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '1px',
          bgcolor: 'rgba(0, 0, 0, 0.12)',
          display: { xs: 'none', md: 'block' }
        }}
      />
      
      <Box sx={{ 
        mb: 0.5,
        textAlign: { xs: 'center', md: 'left' },
        width: '100%',
        display: 'flex',
        flexDirection: "row",
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontSize: { xs: '1.1rem', md: '1.2rem' },
            fontWeight: 600,
            color: '#333',
            pb: 0.5,
            pl: { xs: 0 },
            pr: { md: 1 }
          }}
        >
          {toSentenceCase(sectionName)}
        </Typography>
        
        {/* Show PRD generation buttons only on the Foundation section, positioned to the left of "+" icon */}
        {sectionName === 'Foundation' && projectId && (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            ml: 2
          }}>
            <PRDGenerationButtons projectId={projectId} />
          </Box>
        )}
      </Box>
      
      {/* Section Orchestrator in new style */}
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: { xs: 'center', md: 'flex-start' },
        pr: { md: 3 },
        ml: { md: 0 },
        mt: { xs: 1, md: 2 }
      }}>
        <SectionOrchestrator
          sectionId={sectionId}
          sectionName={toSentenceCase(sectionName)}
          avatarUrl={sectionAvatar.avatarUrl}
          avatarName={sectionAvatar.avatarName}
          projectId={projectId}
          onAvatarChange={handleAvatarChange}
          // Pass consolidated data to prevent additional API calls in SectionOrchestrator
          consolidatedOrchestrator={consolidatedOrchestrator}
          useConsolidatedData={useConsolidatedData}
        />
      </Box>
    </Box>
  );
};

SectionHeader.propTypes = {
  sectionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  sectionId: PropTypes.string,
  projectId: PropTypes.string,
  consolidatedAvatar: PropTypes.object,
  consolidatedOrchestrator: PropTypes.object,
  useConsolidatedData: PropTypes.bool
};

export default React.memo(SectionHeader);